/**
 * Super Admin Module API - Tenants
 * 
 * Database operations for tenant management.
 * Uses core schema for tenant data.
 */

import { supabase } from '@/integrations/supabase/client';
import type { 
  Tenant, 
  TenantFactory, 
  CreateTenantInput, 
  UpdateTenantInput 
} from '../types';

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches all tenants ordered by name.
 */
export async function fetchAllTenants(): Promise<Tenant[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenants')
    .select('*')
    .order('name');

  if (error) throw error;
  return data as Tenant[];
}

/**
 * Fetches a single tenant by ID.
 */
export async function fetchTenantById(tenantId: string): Promise<Tenant | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenants')
    .select('*')
    .eq('id', tenantId)
    .maybeSingle();

  if (error) throw error;
  return data as Tenant | null;
}

/**
 * Fetches all factories grouped for display with tenants.
 */
export async function fetchAllFactories(): Promise<TenantFactory[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('factories')
    .select('id, tenant_id, name, code, is_active')
    .order('name');

  if (error) throw error;
  return data as TenantFactory[];
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Creates a new tenant.
 */
export async function createTenant(input: CreateTenantInput): Promise<Tenant> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenants')
    .insert({
      name: input.name,
      code: input.code.toUpperCase(),
      microsoft_tenant_id: input.microsoft_tenant_id || null,
      settings: input.settings || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Tenant;
}

/**
 * Updates an existing tenant.
 */
export async function updateTenant(tenantId: string, input: UpdateTenantInput): Promise<Tenant> {
  const updateData: Record<string, unknown> = {};
  
  if (input.name !== undefined) updateData.name = input.name;
  if (input.code !== undefined) updateData.code = input.code.toUpperCase();
  if (input.microsoft_tenant_id !== undefined) updateData.microsoft_tenant_id = input.microsoft_tenant_id || null;
  if (input.settings !== undefined) updateData.settings = input.settings;
  if (input.is_active !== undefined) updateData.is_active = input.is_active;

  const { data, error } = await supabase
    .schema('core')
    .from('tenants')
    .update(updateData)
    .eq('id', tenantId)
    .select()
    .single();

  if (error) throw error;
  return data as Tenant;
}

/**
 * Deletes a tenant (use with caution - cascades to all related data).
 */
export async function deleteTenant(tenantId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('tenants')
    .delete()
    .eq('id', tenantId);

  if (error) throw error;
}
